import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  BaseChartDirective,
  provideCharts,
  withDefaultRegisterables,
} from "ng2-charts";
import { ChartData, ChartOptions } from "chart.js";
import { CourtReservationService } from "../../services/court-reservation.service";
import { UserService } from "../../services/user.service";
import { finalize } from "rxjs/operators";
import { forkJoin } from "rxjs";
import { ClubService } from "../../services/club.service";
import { AuthenticationService } from "../../services/authentication.service";
import { PermissionEnum } from "../../model/permission-enum";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { CourtService } from "../../services/court.service";

interface FrequentPlayer {
  id: number;
  fullName: string;
  username: string;
  email: string;
  reservations: number;
  isVip: boolean;
  vipNote: string;
}

@Component({
  selector: "app-analytics",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BaseChartDirective,
    DialogModule,
    InputTextModule,
    ButtonModule,
  ],
  providers: [provideCharts(withDefaultRegisterables())],
  templateUrl: "./analytics.component.html",
  styleUrl: "./analytics.component.css",
})
export class AnalyticsComponent implements OnInit {
  frequentPlayers: FrequentPlayer[] = [];
  sortColumn: string = "reservations";
  sortAsc: boolean = false;

  isLoading: boolean = false;

  startDate: string = "";
  endDate: string = "";

  dailyPopulationData: ChartData<"line"> = {
    labels: [],
    datasets: [{ data: [], label: "Reservations" }],
  };
  weeklyOccupancyData: ChartData<"bar"> = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [{ data: [], label: "Occupancy %" }],
  };
  courtUsageData: ChartData<"bar"> = {
    labels: [],
    datasets: [{ data: [], label: "Reservations" }],
  };

  dailyChartOptions: ChartOptions<"line"> = { responsive: true };

  chartOptions: ChartOptions<"bar"> = { responsive: true };

  vipIds: Set<number> = new Set<number>();
  vipNotes: Map<number, string> = new Map<number, string>();
  accessDenied = false;
  courtIdToName: Map<number, string> = new Map<number, string>();

  constructor(
    private reservationService: CourtReservationService,
    private clubService: ClubService,
    private authService: AuthenticationService,
    private userService: UserService,
    private courtService: CourtService
  ) {}

  clubId: number | null = null;
  clubInfo: any = null;

  ngOnInit(): void {
    const current = this.authService.currentUserValue;
    let permissions = this.authService.currentUserPermissions;
    let hasAdminPermission = permissions.some(
      (permission: PermissionEnum) => permission == PermissionEnum.Admin
    );
    if (!current || !hasAdminPermission) {
      this.accessDenied = true;
      return;
    }

    this.userService.getUser(current.id).subscribe({
      next: (u) => {
        this.clubId = u.club?.id ?? u.club_id ?? null;
        this.clubInfo = u.club || {
          work_hours_start: 8,
          work_hours_end: 22,
          number_of_courts: 1,
        };
        if (this.clubId) {
          this.setQuickRange(30);
        }
      },
      error: () => {
        this.accessDenied = true;
      },
    });
  }

  setQuickRange(days: number): void {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    this.startDate = start.toISOString().split("T")[0];
    this.endDate = end.toISOString().split("T")[0];
    this.loadAnalytics();
  }

  loadAnalytics(): void {
    if (!this.startDate || !this.endDate || !this.clubId) {
      return;
    }

    this.isLoading = true;
    forkJoin({
      reservations: this.reservationService.getReservationsForClubDateRange(
        this.clubId,
        this.startDate,
        this.endDate,
        "Approved"
      ),
      vip: this.clubService.getVipUsers(this.clubId),
      courts: this.courtService.getCourtsForClub(this.clubId),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(({ reservations, vip, courts }) => {
        this.vipNotes = new Map(
          vip.map((v: any) => [v.id, v.verified_as_vip_note || ""])
        );
        this.vipIds = new Set(this.vipNotes.keys());
        this.courtIdToName = new Map(
          (courts || []).map((c: any) => [c.id, c.name])
        );
        this.processReservations(reservations);
      });
  }

  processReservations(reservations: any[]): void {
    const playerMap: { [key: number]: FrequentPlayer } = {};
    const dailyMap: { [key: string]: number } = {};
    const weekdayMap: { [key: number]: number } = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
    };
    const courtMap: { [key: string]: number } = {};
    this.courtIdToName.forEach((name) => (courtMap[name] = 0));

    reservations.forEach((r) => {
      const date = r.reservation_start_time?.split("T")[0];
      if (date) {
        dailyMap[date] = (dailyMap[date] || 0) + 1;
        const wd = new Date(r.reservation_start_time).getDay();
        weekdayMap[wd] = (weekdayMap[wd] || 0) + 1;
      }
      const courtName = this.courtIdToName.get(r.court_id);
      if (courtName) {
        courtMap[courtName] = (courtMap[courtName] || 0) + 1;
      }
      (r.courtReservationPlayer || []).forEach((p: any) => {
        if (!playerMap[p.player_id]) {
          playerMap[p.player_id] = {
            id: p.player_id,
            fullName: p.player?.full_name || "",
            username: p.player?.username || "",
            email: p.player?.email || "",
            reservations: 0,
            isVip: false,
            vipNote: "",
          };
        }
        playerMap[p.player_id].reservations += 1;
      });
    });

    this.frequentPlayers = Object.values(playerMap)
      .map((p) => ({
        ...p,
        isVip: this.vipIds.has(p.id),
        vipNote: this.vipNotes.get(p.id) || "",
      }))
      .sort((a, b) => b.reservations - a.reservations);

    const start = new Date(this.startDate);
    const end = new Date(this.endDate);
    const dayLabels: string[] = [];
    const dayCounts: number[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().split("T")[0];
      dayLabels.push(iso);
      dayCounts.push(dailyMap[iso] || 0);
    }
    this.dailyPopulationData = {
      labels: dayLabels,
      datasets: [{ data: dayCounts, label: "Reservations" }],
    };

    const club = this.clubInfo || {
      work_hours_start: 8,
      work_hours_end: 22,
      number_of_courts: 1,
    };
    const totalHours =
      (club.work_hours_end - club.work_hours_start) * club.number_of_courts;
    this.weeklyOccupancyData = {
      labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      datasets: [
        {
          data: [1, 2, 3, 4, 5, 6, 0].map((d) => {
            const count = weekdayMap[d] || 0;
            return totalHours ? Math.round((count / totalHours) * 100) : 0;
          }),
          label: "Occupancy %",
        },
      ],
    };

    const courts = Object.entries(courtMap);
    this.courtUsageData = {
      labels: courts.map(([name]) => name),
      datasets: [{ data: courts.map(([, c]) => c), label: "Reservations" }],
    };
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortColumn = column;
      this.sortAsc = true;
    }
    const dir = this.sortAsc ? 1 : -1;
    this.frequentPlayers.sort((a: any, b: any) => {
      if (a[column] < b[column]) return -1 * dir;
      if (a[column] > b[column]) return 1 * dir;
      return 0;
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return "pi pi-sort";
    }
    return this.sortAsc ? "pi pi-sort-up" : "pi pi-sort-down";
  }

  vipDialogVisible = false;
  vipNoteText = "";
  vipPlayer: FrequentPlayer | null = null;

  toggleVip(player: FrequentPlayer): void {
    if (!this.clubId) return;
    if (player.isVip) {
      this.clubService.removeVipUser(this.clubId, player.id).subscribe(() => {
        player.isVip = false;
        player.vipNote = "";
      });
    } else {
      this.vipPlayer = player;
      this.vipNoteText = "";
      this.vipDialogVisible = true;
    }
  }

  submitVip(): void {
    if (!this.clubId || !this.vipPlayer) {
      this.vipDialogVisible = false;
      return;
    }
    const note = this.vipNoteText.trim() || "No VIP note";
    this.clubService.addVipUser(this.clubId, this.vipPlayer.id, note).subscribe(
      () => {
        this.vipPlayer!.isVip = true;
        this.vipPlayer!.vipNote = note;
        this.vipDialogVisible = false;
        this.vipPlayer = null;
      },
      () => {
        this.vipDialogVisible = false;
        this.vipPlayer = null;
      }
    );
  }
}
